import LS from 'utils/bom-dom-manipulation/local-storage';
import { BETSLIP_EVENT_TEMPLATE } from 'utils/constants/betslip/betslip-event-template';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { TBetslipEvent, TSuspendReason } from 'interfaces/betslip';
import {
  Competition,
  Game,
  Region,
  Sport
} from 'interfaces/sportsbook-data-levels';

type TData = {
  game?: Record<string, Game>;
  sport?: Record<string, Sport>;
  region?: Record<string, Region>;
  competition?: Record<string, Competition>;
};

export const formatEventData = function (
  data: TData,
  eventIdToUpdate: number
): Record<string, TBetslipEvent> | null {
  const suspendedGamesReasons = JSON.parse(
    LS.getItem(storageKeyName('betslip', 'BETSLIP_SUSPENDED_EVENTS'))
  ) as Record<string, TSuspendReason[]>;

  let modifiedEvent: TBetslipEvent | undefined;

  const sport = data?.sport?.[Object.keys(data?.sport || {})[0]];
  const region = sport?.region?.[Object.keys(sport.region || {})[0]];

  const competition =
    region?.competition?.[Object.keys(region?.competition || {})[0]];

  const gameObj = competition?.game;

  const suspendReasons: TSuspendReason[] = [];

  if (eventIdToUpdate && suspendedGamesReasons?.[eventIdToUpdate]) {
    suspendReasons.push(...suspendedGamesReasons?.[eventIdToUpdate]);
  }

  if (gameObj) {
    const gameId = Object.keys(gameObj)[0];
    const game = gameObj[gameId];

    if (game === null) {
      modifiedEvent = {
        suspendReasons: [...suspendReasons, 'gameEnded']
      } as TBetslipEvent;

      return { [eventIdToUpdate]: modifiedEvent };
    }

    const type = game.type;
    const info = game.info;
    const market =
      'market' in game
        ? game.market === null
          ? null
          : Object.values(game.market || {})[0] || null
        : undefined;

    const eventObj = market?.event;
    const sportAlias = game.sport_alias;

    if (suspendReasons.includes('gameBlocked')) {
      if (game.is_blocked === 0) {
        suspendReasons.splice(suspendReasons.indexOf('gameBlocked'), 1);
      }
    } else {
      if (game.is_blocked === 1) {
        suspendReasons.push('gameBlocked');
      }
    }

    if (market === null && !suspendReasons.includes('market')) {
      suspendReasons.push('market');
    }

    if (market && suspendReasons.includes('market')) {
      suspendReasons.splice(suspendReasons.indexOf('market'), 1);
    }

    if (eventObj) {
      const event = eventObj[eventIdToUpdate];

      if (event) {
        modifiedEvent = {
          ...BETSLIP_EVENT_TEMPLATE,
          ...event
        };

        if (event === null || event.is_blocked) {
          const reason =
            event?.is_blocked === 1 ? 'eventBlocked' : 'eventRemoved';

          if (!suspendReasons.includes(reason)) {
            suspendReasons.push(reason);
          }
        }

        if (suspendReasons.length) {
          const itemsToRemove: TSuspendReason[] = [];

          suspendReasons.forEach(suspendReason => {
            let shouldRemove = false;

            switch (suspendReason) {
              case 'eventBlocked':
                shouldRemove = event.is_blocked === 0;
                break;

              case 'eventRemoved':
                shouldRemove = !!event;
                break;

              case 'other':
                shouldRemove = true;
                break;
            }

            if (shouldRemove) {
              itemsToRemove.push(suspendReason);
            }
          });

          itemsToRemove.forEach(item => {
            suspendReasons.splice(suspendReasons.indexOf(item), 1);
          });
        }

        modifiedEvent.suspendReasons = suspendReasons.length
          ? suspendReasons
          : null;

        if (type !== undefined) {
          modifiedEvent.isLive = type === 1;
        } else {
          delete modifiedEvent.isLive;
        }

        if (modifiedEvent.name) {
          modifiedEvent.event_name = modifiedEvent.name;
        }

        if (info?.current_game_state) {
          modifiedEvent.game_state = info.current_game_state;
          modifiedEvent.score1 = info.score1;
          modifiedEvent.score2 = info.score2;
        }

        if (info?.current_game_time) {
          modifiedEvent.game_time = info.current_game_time;
        }

        if (game.start_ts) {
          modifiedEvent.game_start_date = game.start_ts;
        }

        if (sport?.id) {
          modifiedEvent.sport_id = Number(sport.id);
        }

        if (game.team1_name) {
          modifiedEvent.game_name = `${game.team1_name}${
            game.team2_name ? ` - ${game.team2_name}` : ''
          }`;

          modifiedEvent.team1 = game.team1_name;
        }

        if (game.team2_name) {
          modifiedEvent.team2 = game.team2_name;
        }

        if (market?.name) {
          modifiedEvent.market_name = market.name;
        }

        if (market?.express_id || market?.prematch_express_id) {
          modifiedEvent.express_id =
            market.prematch_express_id || market.express_id;
        }

        if (region?.name) {
          modifiedEvent.region_name = region.name;
          modifiedEvent.region_id = region.id;
        }

        if (competition?.name) {
          modifiedEvent.competition_name = competition.name;
          modifiedEvent.competitionId = competition.id;
        }

        if (sportAlias) {
          modifiedEvent.sport_alias = sportAlias;
          modifiedEvent.sport_index = sportAlias;
        }

        if (sport?.name) {
          modifiedEvent.sport_name = sport.name;
        }
      }
    } else {
      modifiedEvent = {
        ...BETSLIP_EVENT_TEMPLATE,
        ...(modifiedEvent || {})
      };

      if (suspendReasons.length) {
        const itemsToRemove: TSuspendReason[] = [];

        suspendReasons.forEach(reason => {
          if (reason === 'other') {
            itemsToRemove.push(reason);
          }
        });

        itemsToRemove.forEach(item => {
          suspendReasons.splice(suspendReasons.indexOf(item), 1);
        });
      }

      modifiedEvent.suspendReasons = suspendReasons?.length
        ? suspendReasons
        : null;

      if (type !== undefined) {
        modifiedEvent.isLive = type === 1;
      } else {
        delete modifiedEvent.isLive;
      }

      if (info?.current_game_state) {
        modifiedEvent.game_state = info.current_game_state;
        modifiedEvent.score1 = info.score1;
        modifiedEvent.score2 = info.score2;
      }

      if (competition?.name) {
        modifiedEvent.competitionId = competition.id;
      }

      if (info?.current_game_time && modifiedEvent) {
        modifiedEvent.game_time = info.current_game_time;
      }
    }
  } else {
    modifiedEvent = {
      ...BETSLIP_EVENT_TEMPLATE,
      suspendReasons: [...suspendReasons, 'other']
    } as TBetslipEvent;
  }

  if (modifiedEvent) {
    return { [eventIdToUpdate]: modifiedEvent };
  }

  return null;
};
